import React, { ChangeEvent } from 'react';
import { List, ListItem } from '@mui/material';
import { BookingStatus } from '../../../../Models/DTO/Booking.models';
import { SortingOptions } from '../../../../Helpers/Sorting.helpers';
import { BookingListItem } from './BookingListItem';
import BookinglistToolbarContainer from './BookinglistToolbar.container';
import { useBookingList } from './useBookingListHook';
import { BookingListStyles } from './BookingList.styles';
import { Button, Dialog, Dots } from '~/UIKit';
import CloseCheckInDialog from '../Components/CloseCheckInDialog.component';
import { useBookingListModals } from './useBookingListModals';
import { Route } from '~/Constants/Routes.constants';
import { Add } from '@mui/icons-material';
import { BookingDialog } from '../BookingDialog';
import { DynamicNavigation, Error } from '~/Components';
import { AccessList, useHasAccess } from '~/Hooks';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { selectErrorByKey } from '~/store/app';
import { useSelector } from 'react-redux';

export const BookingList = () => {
  const {
    t,
    sortedBookings,
    bookingsCount,
    cancelledBookings,
    waitingBookings,
    handleActionSelection,
    handleMarkSingleBookingAs,
    handleSelectModeChange,
    handleUpdateBooking,
    selectedBookings,
    availableActions,
    isCheckInOpen,
    selectAll,
    selectSingle,
    selectMode,
    setSortBy,
    onlyShow,
    handleTabSelection,
    loadEventBookings,
    isBookingsLoading,
  } = useBookingList();

  const {
    totalBookings,
    presentBookings,
    handleCheckinModalCancel,
    totalSeats,
    showCheckInCloseDialog,
    handleCheckinDialogToggle,
    mobile,
    redirectTo,
    toggleBookingDialog,
    closeBookingDialog,
    showBookingDialog,
  } = useBookingListModals();

  const { hasAccess } = useHasAccess();

  const onActionSelected = (action: any) => handleActionSelection(action);
  const onSortSelected = (val: string) => setSortBy(val);

  const { classes } = BookingListStyles();
  const handleSortSelect = (event: ChangeEvent<{ value: string }>) => {
    onSortSelected(event.target.value);
  };

  const handleToggleBookingDialog = () => {
    if (mobile) {
      redirectTo(Route.BOOK_CUSTOMER);
    } else {
      toggleBookingDialog();
    }
  };

  const MenuItems = [
    {
      value: BookingStatus.BOOKED,
      label: `${bookingsCount} ${t('app.bookings.all')}`,
    },
    hasAccess(AccessList.WAITING_LIST)
      ? {
          value: BookingStatus.WAITING_LIST,
          label: `${waitingBookings} ${t('app.bookings.waiting')}`,
        }
      : undefined,
    hasAccess(AccessList.CANCELLATION_LIST)
      ? {
          value: BookingStatus.CANCELLED,
          label: `${cancelledBookings} ${t('app.bookings.cancelled')}`,
        }
      : undefined,
  ];
  const statusError = useSelector(selectErrorByKey(REQUEST_ACTIONS.BOOKING_UPDATE_SINGLE));
  return (
    <div className={classes.BookingListContainer} data-testid="BookingList">
      <div className={classes.BookingListToolbar}>
        <div className={classes.BookingListToolbarContainer}>
          <div className={classes.ToolbarTop}>
            <DynamicNavigation<String>
              onChange={handleTabSelection}
              value={onlyShow}
              className={classes.BookingListSelect}
              items={MenuItems}
            />

            {hasAccess(AccessList.MANUAL_BOOKING) && (
              <Button
                data-testid="OpenCheckInButton"
                onClick={handleCheckinDialogToggle}
                variant="contained"
                size="small"
              >
                {!isCheckInOpen ? t('app.checkin.open') : t('app.checkin.close')}
              </Button>
            )}
          </div>
          <BookinglistToolbarContainer
            actions={availableActions}
            handleSortSelect={handleSortSelect}
            onActionSelected={onActionSelected}
            onSelectAll={selectAll}
            onSelectModeChanged={handleSelectModeChange}
            selectMode={selectMode}
            selectedAmount={selectedBookings.length}
            sortOptions={SortingOptions}
            t={t}
            totalAmount={sortedBookings.length}
          />
        </div>
        {hasAccess(AccessList.MANUAL_BOOKING) && (
          <ListItem
            button
            className={classes.openBookingDialogButton}
            dense
            key={'OpenBookingDialogButton'}
            data-testid="OpenBookingDialogButton"
            onClick={handleToggleBookingDialog}
          >
            <div className={classes.openBookingDialogIcon}>
              <Add />
            </div>
            {t('app.customerList.new')}
          </ListItem>
        )}
      </div>
      <List className={classes.bookingListBookingList}>
        <Error error={statusError} />
        {isBookingsLoading && (
          <div className={classes.loading}>
            <Dots />
          </div>
        )}
        {sortedBookings.length === 0 && !isBookingsLoading && (
          <div>
            <div className={classes.emptyBookings}>
              <span className={classes.emptyBookingsHeader}>{t('app.bookings.empty')}</span>
              <span>{t('app.bookings.createbooking')}</span>
              <span>{t('app.bookings.createbooking2')}</span>
            </div>
          </div>
        )}
        {!isBookingsLoading &&
          sortedBookings.map((booking, index) => (
            <BookingListItem
              booking={booking}
              checkInOpen={isCheckInOpen}
              key={index}
              markPresence={handleMarkSingleBookingAs}
              onSelected={selectSingle}
              selectable={selectMode}
              selected={selectedBookings.includes(booking.uuid)}
              updateBookingStatus={handleUpdateBooking}
            />
          ))}
      </List>
      {showCheckInCloseDialog && (
        <CloseCheckInDialog
          bookingInformation={{
            booked: totalBookings,
            present: presentBookings,
            total: totalSeats,
            none: totalBookings - presentBookings,
          }}
          onClose={handleCheckinModalCancel}
          onConfirmCloseCheckIn={handleCheckinDialogToggle}
        />
      )}
      {showBookingDialog && (
        <Dialog
          classes={{
            paper: classes.bookingDialog,
          }}
          onClose={handleToggleBookingDialog}
          open={showBookingDialog}
        >
          <BookingDialog onClose={closeBookingDialog} loadEventBookings={loadEventBookings} />
        </Dialog>
      )}
    </div>
  );
};
